import React from 'react';
import moment from 'moment';
import I18n from 'i18n-js';
import { RouteComponentProps } from 'react-router';
import { get } from 'lodash';
import gql from 'graphql-tag';
import { graphql, ChildProps } from 'react-apollo';

import CMSListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import URLHelpers, { MarketplacePathContext } from '../../shared/url_helpers';
import { CoreShopContainer } from '../../../schemas/rql';

import ShopHeader, { ShopHeaderFragment } from './shop_header';
import ShopMetadata, { ShopMetadataFragment } from './shop_metadata';
import ShopTabs, { ShopTabsFragment } from './tabs';

type RouteProps = RouteComponentProps<MarketplacePathContext, {}>;
type Props = ChildProps<RouteProps, CoreShopContainer.Query, CoreShopContainer.Variables>;

function getSubtitle(s: CoreShopContainer.Sales) {
  const date = moment.unix(s.endsAt.seconds).format('MMMM D');
  const { percent } = s;
  return I18n.t('discovery.shop.saleRow.subtitle', { date, percent });
}

export class ShopContainer extends React.Component<Props, null> {
  renderSaleRows() {
    if (!this.props.data.shop || !this.props.data.shop.sales) return [];
    return this.props.data.shop.sales.map((sale) => {
      // Note that "showCode" here maps to "sale.hidden?"
      // So if it is TRUE, we should NOT render because it IS hidden.
      if (sale.buyerIneligibilityReason || sale.showCode) { return null; }

      return (
        <CMSListingsCollection
          key={sale.slug}
          title={sale.name}
          saleSlug={sale.slug}
          ctaTargetHref={URLHelpers.salePath(sale.slug)}
          subtitle={getSubtitle(sale)}
        />
      );
    });
  }

  render() {
    return (
      <div>
        <ShopMetadata shop={this.props.data.shop} />
        <ShopHeader shop={this.props.data.shop} />
        {this.renderSaleRows()}
        <div className="scaling-mb-3">
          <ShopTabs shop={this.props.data.shop} listingCount={get(this.props.data, 'shopListings.total')} />
        </div>
        {this.props.children}
      </div>
    );
  }
}

const connect = graphql<
RouteProps,
CoreShopContainer.Query,
CoreShopContainer.Variables
>(
  gql`
    query Core_Shop_Container($shop: String) {
      shop(input: { slug: $shop }) {
        _id
        id
        slug
        freeDomesticShippingRate {
          regionCode
          freeShippingThreshold {
            display
          }
        }
        sales {
          slug
          name
          endsAt {
            seconds
          }
          percent
          buyerIneligibilityReason
          showCode
        }
        ... ShopHeaderFragment
        ... ShopMetadataFragment
        ... ShopTabsFragment
      }
      shopListings: listingsSearch(input: { shopSlug: $shop }) {
        total
      }
    }
    ${ShopHeaderFragment}
    ${ShopTabsFragment}
    ${ShopMetadataFragment}
  `,
  {
    options: (props) => ({
      ssr: true,
      variables: {
        shop: props.params.shop,
      },
    }),
  },
);

export default connect(ShopContainer);
